import styled from '@emotion/styled';
import { Spacings } from '@styles/index';
import * as React from 'react';
import { IGroupUser } from 'types/groups/index';
import H5 from '@base/H5/H5';
import H7 from '@base/H7/H7';
import GroupsUserAvatar from './GroupsUserAvatar';

interface IGroupsUserProgressProps {
  user: IGroupUser;
  avatarSize: string;
  outerProgressSize: string;
  innerProgressSize: string;
  outerDashOffset: number;
  innerDashOffset: number;
  name?: string;
  type?: string;
  onClick?: (event: any) => void;
}

const Wrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const ProgressArcs = styled.svg`
  height: ${(props) => props.fontSize};
  width: ${(props) => props.fontSize};
  position: ${(props) => (props.type === 'outer' ? 'initial' : 'absolute')};
  overflow: visible;
  top: ${(props) => (props.type === 'outer' ? 0 : Spacings.SPACING_2B)};
`;

const StyProgress = styled.g`
  transform: rotate(135deg);
  transform-origin: center center;
`;

const ProgressPathEmpty = styled.circle`
  fill: none;
  stroke-linecap: round;
  stroke: var(--color-divider);
  stroke-width: 4;
  stroke-dasharray: ${(props) => `${props.offset} 300`};
`;

const ProgressPath = styled.circle`
  fill: none;
  stroke-linecap: round;
  stroke-width: 4;
  stroke: ${(props) => props.color};
  stroke-dasharray: ${(props) => Number(props.offset)} 300;
`;

const StyGroupsUserAvatar = styled.div`
  position: absolute;
  top: ${Spacings.SPACING_4B};
`;

const StyH5 = styled(H5)`
  position: absolute;
  top: 78px;
  text-align: center;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const StyH7 = styled(H7)`
  position: absolute;
  top: ${Spacings.SPACING_16B};
  text-align: center;
  width: ${Spacings.SPACING_23B};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const GroupsUserProgress: React.FC<IGroupsUserProgressProps> = ({
  user,
  avatarSize,
  outerProgressSize,
  innerProgressSize,
  outerDashOffset,
  innerDashOffset,
  name,
  type,
  onClick
}) => {
  const {
    firstName,
    watchMins,
    watchMinsThreshold,
    questionsAttemptThreshold,
    questionsAttempted
  } = user;

  const questionsAttemptedProgress =
    questionsAttempted < questionsAttemptThreshold
      ? (questionsAttempted / questionsAttemptThreshold) * 100
      : 100;
  const watchMinsProgress =
    watchMins < watchMinsThreshold
      ? (watchMins / watchMinsThreshold) * 100
      : 100;

  return (
    <Wrapper>
      <ProgressArcs type="outer" fontSize={outerProgressSize}>
        <StyProgress>
          <ProgressPathEmpty
            cx="50%"
            cy="50%"
            r="50%"
            offset={outerDashOffset}
          />
          <ProgressPath
            cx="50%"
            cy="50%"
            r="50%"
            color="var(--color-i-blue)"
            offset={(watchMinsProgress * outerDashOffset) / 100}
          />
        </StyProgress>
      </ProgressArcs>
      <ProgressArcs type="inner" fontSize={innerProgressSize}>
        <StyProgress>
          <ProgressPathEmpty
            cx="50%"
            cy="50%"
            r="50%"
            offset={innerDashOffset}
          />
          <ProgressPath
            cx="50%"
            cy="50%"
            r="50%"
            color="var(--color-i-green)"
            offset={(questionsAttemptedProgress * innerDashOffset) / 100}
          />
        </StyProgress>
      </ProgressArcs>
      <StyGroupsUserAvatar onClick={onClick}>
        <GroupsUserAvatar
          user={user}
          length={1}
          size={avatarSize}
          type={type === 'drawer' ? 'default' : 'overlay'}
        />
      </StyGroupsUserAvatar>
      {type === 'drawer' ? <StyH7>{name}</StyH7> : <StyH5>{firstName}</StyH5>}
    </Wrapper>
  );
};

export default GroupsUserProgress;
